import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { PHOTO_UPLOAD } from '@/constants/testIds';
import { validateUserFiles, USER_UPLOAD_ACCEPT_ATTR, MAX_FILE_SIZE_LABEL } from '@/lib/fileValidation';
import { Upload, Image as ImageIcon, FileText, Trash2, Download, CheckCircle2, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// canDownload controls whether the per-file Download button is enabled
// (typically false until payment is made / admin approves).
export default function PhotoUploadGallery({
  inspectionId,
  existingPhotos = [],
  onUploadComplete,
  canDownload = true,
  downloadLockedReason = ''
}) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');

    const { valid, errors } = validateUserFiles(files);
    if (errors.length > 0) {
      setError(errors.join(' '));
      if (valid.length === 0) {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    try {
      for (const file of valid) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(
          `${API}/inspections/${inspectionId}/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePick = () => fileInputRef.current?.click();

  const handleDelete = async (path) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    setDeleting((d) => ({ ...d, [path]: true }));
    try {
      await axios.delete(`${API}/inspections/${inspectionId}/photo`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { path }
      });
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting((d) => ({ ...d, [path]: false }));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-sm font-semibold text-black flex items-center gap-2">
          <ImageIcon className="w-4 h-4" />
          Photos &amp; Documents ({existingPhotos.length})
        </h4>
        <div className="flex items-center gap-2">
          {savedFlash && (
            <span data-testid="photo-saved-flash" className="text-xs text-green-700 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Saved
            </span>
          )}
          <button
            type="button"
            data-testid={PHOTO_UPLOAD.uploadButton}
            onClick={handlePick}
            disabled={uploading}
            className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 transition-all flex items-center gap-1 disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" />
            {uploading ? 'Saving…' : 'Save / Upload'}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        data-testid={PHOTO_UPLOAD.fileInput}
        type="file"
        accept={USER_UPLOAD_ACCEPT_ATTR}
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800">
          {error}
        </div>
      )}

      {!canDownload && existingPhotos.length > 0 && (
        <div
          data-testid="photo-download-locked-banner"
          className="p-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900 flex items-start gap-2"
        >
          <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Downloads are currently locked.{' '}
            {downloadLockedReason ||
              'Payment must be received and approved by All Build Consult administration first.'}
          </span>
        </div>
      )}

      {existingPhotos.length === 0 ? (
        <div
          onClick={handlePick}
          className="border-2 border-dashed border-slate-300 rounded-sm p-8 text-center text-sm text-slate-500 cursor-pointer hover:bg-slate-50 transition-all"
        >
          <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <p>Click to upload photos or PDF documents</p>
          <p className="text-xs mt-1">JPEG or PDF only. Max {MAX_FILE_SIZE_LABEL} per file.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {existingPhotos.map((path) => {
            const isPdf = path.toLowerCase().endsWith('.pdf');
            const fileName = path.split('/').pop() || 'file';
            return (
              <div
                key={path}
                data-testid={PHOTO_UPLOAD.photoItem}
                className="relative aspect-square bg-slate-100 rounded-sm overflow-hidden group border border-slate-200"
              >
                {isPdf ? (
                  <div className="w-full h-full flex flex-col items-center justify-center p-2 bg-slate-50">
                    <FileText className="w-8 h-8 text-slate-500 mb-1" />
                    <span className="text-[10px] text-slate-700 truncate w-full text-center">{fileName}</span>
                  </div>
                ) : (
                  <img
                    src={`${API}/files/${path}?auth=${token}`}
                    alt="Inspection"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end justify-end gap-1 p-1.5 opacity-0 group-hover:opacity-100">
                  {canDownload && (
                    <a
                      data-testid="photo-download-btn"
                      href={`${API}/files/${path}?auth=${token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download"
                      className="w-7 h-7 bg-white/95 rounded-sm flex items-center justify-center hover:bg-white"
                    >
                      <Download className="w-3.5 h-3.5 text-slate-700" />
                    </a>
                  )}
                  {!canDownload && (
                    <span
                      title="Download locked"
                      className="w-7 h-7 bg-white/80 rounded-sm flex items-center justify-center cursor-not-allowed"
                    >
                      <Lock className="w-3.5 h-3.5 text-slate-400" />
                    </span>
                  )}
                  <button
                    data-testid="photo-delete-btn"
                    type="button"
                    onClick={() => handleDelete(path)}
                    disabled={deleting[path]}
                    title="Delete"
                    className="w-7 h-7 bg-white/95 rounded-sm flex items-center justify-center hover:bg-rose-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-rose-700" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
